/**
 * Summary strip above the timeline: what is planned, what is done, and what is
 * happening right now.
 */
import { useMemo } from "react";
import { Clock, CheckCircle2, Timer, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { formatDuration, formatMinute } from "@/lib/dates";
import { cn } from "@/lib/utils";
import type { TimelineEntry } from "./block";
import { KINDS, type DashplanKind } from "./constants";

export function DayStats({
  entries,
  nowMinute,
}: {
  entries: TimelineEntry[];
  nowMinute: number | null;
}) {
  const stats = useMemo(() => {
    const planned = entries.reduce((sum, e) => sum + e.durationMinutes, 0);
    const done = entries.filter((e) => e.status === "done");
    const focusLogged = entries.reduce(
      (sum, e) => sum + e.actualFocusMinutes,
      0,
    );

    const sorted = [...entries].sort((a, b) => a.startMinute - b.startMinute);
    const current =
      nowMinute === null
        ? null
        : sorted.find(
            (e) =>
              nowMinute >= e.startMinute &&
              nowMinute < e.startMinute + e.durationMinutes,
          ) ?? null;
    const next =
      nowMinute === null
        ? null
        : sorted.find((e) => e.startMinute > nowMinute) ?? null;

    return {
      planned,
      doneCount: done.length,
      total: entries.length,
      focusLogged,
      current,
      next,
    };
  }, [entries, nowMinute]);

  if (stats.total === 0) return null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        icon={Clock}
        label="Planned"
        value={formatDuration(stats.planned)}
        detail={`${stats.total} block${stats.total === 1 ? "" : "s"}`}
      />
      <Stat
        icon={CheckCircle2}
        label="Done"
        value={`${stats.doneCount}/${stats.total}`}
        detail={
          stats.total > 0
            ? `${Math.round((stats.doneCount / stats.total) * 100)}%`
            : undefined
        }
        tone={stats.doneCount === stats.total ? "good" : undefined}
      />
      <Stat
        icon={Timer}
        label="Focus logged"
        value={formatDuration(stats.focusLogged)}
        detail={stats.focusLogged === 0 ? "none yet" : "actual time"}
      />

      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            <ArrowRight className="w-3.5 h-3.5" />
            {stats.current ? "Right now" : "Next up"}
          </div>
          {stats.current ?? stats.next ? (
            <NowNext entry={(stats.current ?? stats.next)!} />
          ) : (
            <p className="text-sm text-muted-foreground">
              {nowMinute === null ? "Not today" : "Nothing left today"}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NowNext({ entry }: { entry: TimelineEntry }) {
  const kind = KINDS[entry.kind as DashplanKind] ?? KINDS.other;
  const Icon = kind.icon;
  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon className={cn("w-4 h-4 shrink-0", kind.accent)} />
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{entry.title}</p>
        <p className="text-xs text-muted-foreground font-mono tabular-nums">
          {formatMinute(entry.startMinute)} ·{" "}
          {formatDuration(entry.durationMinutes)}
        </p>
      </div>
    </div>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  detail,
  tone,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  detail?: string;
  tone?: "good";
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        <p
          className={cn(
            "text-lg font-semibold tabular-nums",
            tone === "good" && "text-primary",
          )}
        >
          {value}
        </p>
        {detail && (
          <p className="text-xs text-muted-foreground">{detail}</p>
        )}
      </CardContent>
    </Card>
  );
}
